// What a trip's map frames, in one place for both the trip's card on Your trips and the Map itself, so
// the two can't drift apart. Plain functions, used by the server (the card's points) and the web view.

export type LngLat = [number, number];
export type Journey = [LngLat, LngLat];

// Anything with an item's shape: the server's stored records and the web view's items alike.
interface Placed {
  type?: unknown;
  where?: unknown;
  from?: unknown;
  to?: unknown;
}

const at = (w: unknown): LngLat | null => {
  const p = w as { lat?: unknown; lng?: unknown } | null | undefined;
  return typeof p?.lat === "number" && typeof p.lng === "number" ? [p.lng, p.lat] : null;
};

// Where a trip stays and visits (its places, goals and stays, and a move known at one end only), and its
// journeys (a move with both ends).
export function tripShape(items: Placed[]): { points: LngLat[]; routes: Journey[] } {
  const points: LngLat[] = [];
  const routes: Journey[] = [];
  for (const item of items) {
    const from = at(item.from);
    const to = at(item.to);
    if (item.type === "move" && from && to) routes.push([from, to]);
    else {
      const point = at(item.where) ?? from ?? to;
      if (point) points.push(point);
    }
  }
  return { points, routes };
}

// How near a journey's end must be to somewhere the trip stays or visits to count in the frame: a day
// trip's far end (Kyoto to Nara, 35 km) is in, the long way from home is not.
export const NEAR_KM = 150;

// The great-circle distance between two points, in km.
export function km([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): number {
  const rad = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lng2 - lng1) * rad) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// What to frame: where the trip stays and visits, with the ends of journeys within NEAR_KM of those, so
// a day trip stays in the frame and a flight from home runs off the edge instead of pulling the map out
// over the sea; a trip of journeys alone is framed on them.
export function framed({ points, routes }: { points: LngLat[]; routes: Journey[] }): LngLat[] {
  if (!points.length) return routes.flat();
  const near = (end: LngLat) => points.some((p) => km(p, end) <= NEAR_KM);
  return [...points, ...routes.flat().filter(near)];
}
